import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { SiteContext } from '../../store/siteContext';
import { getRowMenuPosition, type MenuPosition } from '../../utils/menuPosition';
import SiteForm from './SiteForm';
import DeleteDialog from '../../utils/components/DeleteDialog';

interface Props {
  selectedSiteId: number | null;
  searchTerm: string;
  onSiteClick: (siteId: number) => void;
}

const SiteColumn: React.FC<Props> = ({ selectedSiteId, searchTerm, onSiteClick }) => {
  const {
    sites,
    getSites,
    editSite,
    editSiteLoading,
    deleteSite,
    deleteSiteLoading,
  } = useContext(SiteContext);
  const { t } = useTranslation();
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [actionSiteId, setActionSiteId] = useState<number | null>(null);
  const [hoveredSiteId, setHoveredSiteId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getSites();
  }, [getSites]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    selectedSiteIdToOpen: number
  ) => {
    event.stopPropagation();
    setMenuPosition(getRowMenuPosition(event, '[data-site-row="true"]'));
    setActionSiteId(selectedSiteIdToOpen);
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
  };

  const handleEditOpen = () => {
    setEditing(true);
    handleMenuClose();
  };

  const handleEditClose = () => {
    setEditing(false);
  };

  const handleDeleteOpen = () => {
    setDeleting(true);
    handleMenuClose();
  };

  const handleDeleteClose = () => {
    setDeleting(false);
  };

  const selectedSite = actionSiteId
    ? sites.find((site) => site.id === actionSiteId)
    : undefined;
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredSites = sites.filter((site) =>
    site.name.toLowerCase().includes(normalizedSearchTerm)
  );

  const handleEditSubmit = (data: SiteFormDto) => {
    if (!selectedSite) {
      return;
    }
    editSite(selectedSite.id, data, () => {
      setEditing(false);
      getSites(true);
    });
  };

  const handleDeleteConfirm = () => {
    if (!selectedSite) {
      return;
    }
    deleteSite(selectedSite.id, () => {
      setDeleting(false);
      getSites(true);
    });
  };

  return (
    <Grid size={{ xs: 3 }} sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
      {filteredSites.map((site) => (
        <Box
          key={site.id}
          onMouseEnter={() => setHoveredSiteId(site.id)}
          onMouseLeave={() => setHoveredSiteId(null)}
          onClick={() => onSiteClick(site.id)}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            minHeight: '30px',
            mb: 1,
            px: 1.5,
            borderRadius: '4px',
            cursor: 'pointer',
            ...theme.typography.usersFontTitle,
            backgroundColor:
              selectedSiteId === site.id
                ? theme.palette.selectedBackground
                : 'transparent'
          })}
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexGrow: 1,
            }}
          >
            {site.name}
          </Box>
          {hoveredSiteId === site.id && (
            <>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, site.id)}
                sx={{ color: 'green', p: '4px' }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      ))}
      <Menu
          anchorReference="anchorPosition"
          anchorPosition={menuPosition || undefined}
          open={Boolean(menuPosition)}
          onClose={handleMenuClose}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
      >
        <MenuItem onClick={handleEditOpen}>{t('common:t.edit')}</MenuItem>
        <MenuItem onClick={handleDeleteOpen}>{t('common:t.delete')}</MenuItem>
      </Menu>

      <Dialog open={editing} onClose={handleEditClose}>
        {selectedSite && (
          <SiteForm
            onSubmit={handleEditSubmit}
            defaultValues={selectedSite}
            buttonText={t('common:t.edit')}
            loading={editSiteLoading}
            cancelButton
            onCancel={handleEditClose}
          />
        )}
      </Dialog>
      <DeleteDialog
        loading={deleteSiteLoading}
        open={deleting}
        onClose={handleDeleteClose}
        ns="sites"
        onConfirm={handleDeleteConfirm}
      />
    </Grid>
  );
};

export default SiteColumn;
